import { AlertTriangle, Check, Download, FileText, Loader2, Maximize2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from '@/lib/notify';

import { useTemplatePreview } from '../../api/useTemplatePreview';
import { useVerifyStep } from '../../api/useVerifyStep';
import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';
import { isSessionExpiredError, SessionExpiredScreen } from '../SessionExpiredScreen';

/**
 * Viewer parameters, appended to the PDF URL.
 *
 * Without them the browser's own PDF chrome — a dark bar with Drive, print and
 * download buttons — sits on top of the document, taking a third of the frame and
 * offering actions that belong to the browser rather than to this flow. The page
 * provides its own Download.
 */
const VIEWER_PARAMS = '#toolbar=0&navpanes=0&scrollbar=0&view=FitH';

/**
 * The applicant's view of a Document Template step.
 *
 * Nothing is verified here — the document is generated from what earlier steps
 * already proved, and the applicant reads it and accepts. So the document IS the
 * screen: anything else would be asking them to confirm something they hadn't seen.
 *
 * The run column is 512px wide and these are dense, small-print forms, so the inline
 * frame is a readable preview and the full-screen viewer is where the document is
 * actually read.
 */
export function TemplateStepRunner({
  session,
  step,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
}) {
  const { data, isLoading, error } = useTemplatePreview(session.id, step.id);
  const { mutate, isPending } = useVerifyStep(session.id);
  const [expired, setExpired] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // When an eSign step follows, accepting isn't the end of the document — it's the
  // gate into signing it. Saying "I have read and accept" there implies the job is
  // done and makes the signing screen that follows look like an extra hurdle.
  const leadsToEsign = useMemo(
    () => session.steps.some((s) => s.stepType === 'esign_template' && s.position > step.position),
    [session.steps, step.position],
  );
  const acceptLabel = leadsToEsign ? 'Proceed for eSign' : 'I have read and accept';

  // Rebuilt only when the document changes — these run to a megabyte, and a new
  // string on every render makes the browser re-parse the whole thing.
  const pdfUrl = useMemo(
    () => (data ? `data:application/pdf;base64,${data.documentBase64}` : null),
    [data],
  );

  // Escape closes the viewer, and the page behind it must not scroll under it.
  useEffect(() => {
    if (!isFullScreen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullScreen(false);
    };
    const previousOverflow = document.body.style.overflow;

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullScreen]);

  function handleAccept() {
    mutate(
      { stepId: step.id, input: { accepted: true } },
      {
        onError: (err) => {
          if (isSessionExpiredError(err)) {
            setExpired(true);
            return;
          }
          toast.error("Couldn't record your acceptance. Please try again.");
        },
      },
    );
  }

  if (expired) {
    return <SessionExpiredScreen />;
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
        <Loader2 size={22} className="animate-spin text-primary" />
        <p className="text-[length:var(--text-14)] text-[#666666]">Preparing your document…</p>
      </div>
    );
  }

  if (error || !data || !pdfUrl) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FDECEC]">
          <AlertTriangle size={24} className="text-destructive" />
        </div>
        <h3 className="text-base font-semibold text-[#041A3A]">Document unavailable</h3>
        <p className="max-w-xs text-[length:var(--text-14)] leading-relaxed text-[#666666]">
          We couldn't prepare your document. Please contact the organisation that sent you this
          link.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <header className="text-center">
          <h3 className="text-base font-semibold text-[#041A3A]">{step.displayName}</h3>
          <p className="mx-auto mt-1 max-w-xs text-[length:var(--text-14)] leading-relaxed text-[#666666]">
            We've filled this in with the details you already provided. Please read it before
            accepting.
          </p>
        </header>

        {data.unresolvedFields.length > 0 ? (
          <p className="flex items-start gap-2 rounded-xl bg-[#FFF7E8] px-3 py-2.5 text-xs leading-relaxed text-[#8A5A00]">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>
              Blank for now: {data.unresolvedFields.join(', ')} — these are completed before
              signing.
            </span>
          </p>
        ) : null}

        {/* Document card: its own chrome, so the frame reads as part of the flow
            rather than as a browser plugin dropped into the page. */}
        <div className="overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_1px_3px_0_rgba(16,24,40,0.06)]">
          <div className="flex items-center gap-2 border-b border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <FileText size={14} className="text-primary" />
            </span>
            <span className="min-w-0 flex-1 truncate text-xs font-medium text-[#041A3A]">
              {data.fileName}
            </span>
            <button
              type="button"
              onClick={() => setIsFullScreen(true)}
              className="flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-primary transition hover:bg-primary/10"
            >
              <Maximize2 size={13} />
              Full screen
            </button>
          </div>

          {/*
            An <object>, not an <iframe>: iOS Safari refuses to render a PDF data:
            URL in an iframe and shows a blank white box, which reads as a broken
            step. <object> falls back to its children, so there is always a way in.
          */}
          <object
            data={`${pdfUrl}${VIEWER_PARAMS}`}
            type="application/pdf"
            title={data.fileName}
            className="block h-[440px] w-full bg-[#F3F4F6]"
          >
            <div className="flex h-[440px] flex-col items-center justify-center gap-3 px-6 text-center">
              <FileText size={26} className="text-[#9CA3AF]" />
              <p className="text-[length:var(--text-14)] text-[#666666]">
                Your browser can't show the document here.
              </p>
              <a
                href={pdfUrl}
                download={data.fileName}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-4 py-2 text-[length:var(--text-14)] font-medium text-[#303030]"
              >
                <Download size={14} />
                Open the document
              </a>
            </div>
          </object>

          <button
            type="button"
            onClick={() => setIsFullScreen(true)}
            className="w-full border-t border-[#E5E7EB] py-2 text-xs font-medium text-[#666666] transition hover:bg-[#F9FAFB] hover:text-primary"
          >
            Tap to read full screen
          </button>
        </div>

        {/* Primary action full-width: on a phone this sits under the thumb, and it
            is the only thing to do on this screen. */}
        <button
          type="button"
          onClick={handleAccept}
          disabled={isPending}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-[length:var(--text-14)] font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
          {isPending ? 'Saving…' : acceptLabel}
        </button>

        <a
          href={pdfUrl}
          download={data.fileName}
          className="inline-flex items-center justify-center gap-1.5 self-center text-xs font-medium text-[#666666] transition hover:text-primary"
        >
          <Download size={13} />
          Download a copy
        </a>
      </div>

      {isFullScreen ? (
        <div className="fixed inset-0 z-[9999] flex flex-col bg-[#1F2430]">
          <div className="flex shrink-0 items-center gap-3 px-4 py-3">
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">
              {data.fileName}
            </span>
            <a
              href={pdfUrl}
              download={data.fileName}
              className="flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white/80 transition hover:bg-white/10 hover:text-white"
            >
              <Download size={14} />
              Download
            </a>
            <button
              type="button"
              onClick={() => setIsFullScreen(false)}
              aria-label="Close full screen"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white/80 transition hover:bg-white/10 hover:text-white"
            >
              <X size={18} />
            </button>
          </div>

          <object
            data={`${pdfUrl}#toolbar=0&navpanes=0&view=FitH`}
            type="application/pdf"
            title={data.fileName}
            className="min-h-0 w-full flex-1 bg-[#1F2430]"
          >
            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-white/70">
              Your browser can't show the document here — use Download instead.
            </div>
          </object>

          <div className="shrink-0 border-t border-white/10 p-3">
            <button
              type="button"
              onClick={() => {
                setIsFullScreen(false);
                handleAccept();
              }}
              disabled={isPending}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-[length:var(--text-14)] font-semibold text-white transition hover:bg-primary/90 disabled:opacity-60"
            >
              <Check size={16} />
              {acceptLabel}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
